<template>
  <q-dialog
    ref="dialogRef"
    v-bind="dialogProps"
    v-model="isVisible"
    :persistent="isPersistent"
    class="base-dialog"
    :maximized="dialogProps?.maximized ?? maximized"
    @hide="onDialogHide"
  >
    <q-card class="full-width">
      <q-card-section
        class="base-diaog-header"
        :class="{'q-pb-lg': $q.screen.gt.xs, 'q-pb-md': $q.screen.lt.sm, [cardSectionPadingClassTop]: true }"
      >
        <div class="row items-center">
          <div class="col">
            <h2 class="col title fs-sm">
              <slot
                name="title-value"
                :title="title"
              >
                {{ title }}
              </slot>
            </h2>
            <div
              v-if="caption && tab !== 'contactRequest'"
              class="caption col"
              :class="{'q-mt-lg': $q.screen.gt.xs, 'q-mt-md': $q.screen.lt.sm}"
              v-html="$sanitize(caption)"
            />
          </div>
          <div class="col-auto q-pl-md self-start">
            <q-btn
              v-if="showCloseIcon"
              v-close-popup
              :aria-label="t('actions.closeDialog')"
              class="base-dialog-close-icon"
              icon="close"
              size="md"
              flat
              dense
            />
          </div>
        </div>
      </q-card-section>
      <q-separator inset />
      <q-tab-panels
        :model-value="tab"
        class="bg-transparent"
        :class="{'q-my-lg': $q.screen.gt.xs, 'q-my-md': $q.screen.lt.sm, [cardSectionPadingClassContent]: true }"
        @update:model-value="updateTab"
      >
        <q-tab-panel
          name="default"
          class="q-pa-none full-width"
          flat
        >
          <slot>
            <p
              v-if="messageHtml"
              :role="statusProps?.role"
              :aria-live="statusProps?.ariaLive"
              v-html="$sanitize(message)"
            />
            <p
              v-else-if="message"
              :role="statusProps?.role"
              :aria-live="statusProps?.ariaLive"
            >
              {{ message }}
            </p>
          </slot>
        </q-tab-panel>
        <slot name="tabPanels" />
      </q-tab-panels>
      <div v-if="orderedActions?.filter(a => a.show !== false).length > 0">
        <q-card-actions
          :vertical="$q.screen.lt.sm"
          :align="$q.screen.lt.sm ? 'center' : 'right'"
          class="q-gutter-y-sm"
          :class="cardSectionPadingClassContent"
        >
          <q-btn
            v-for="action,i in orderedActions"
            v-show="action.show ?? true"
            :key="i"
            :form="action.form"
            :type="action.type"
            :label="action.label"
            :outline="action.outline"
            :color="action.color ?? 'primary'"
            :role="action.loading ? 'status' : 'button'"
            :aria-live="action.loading ? 'polite' : undefined"
            :loading="action.loading"
            :disable="action.disable"
            no-caps
            class="wlk-btn"
            :class="{'full-width': $q.screen.lt.sm}"
            :unelevated="action.btnProps?.unelevated ?? true"
            :aria-label="action.tooltip?.text"
            v-bind="action.btnProps"
            @click="e => action.handler?.(e, { onDialogHide, onDialogOk })"
          />
        </q-card-actions>
      </div>
    </q-card>
  </q-dialog>
</template>
<script setup>
import { computed, ref, watch, toRefs, watchEffect } from 'vue'
import { useQuasar, useDialogPluginComponent } from 'quasar'
import cloneDeep from 'lodash/cloneDeep'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: () => false
  },
  tab: {
    type: String,
    default () { return 'default' },
    validator (value) {
      return ['default', 'contactRequest'].includes(value)
    }
  },
  dialogReference: {
    type: Object,
    default: () => null
  },
  dialogProps: {
    type: Object,
    default: () => { }
  },
  title: {
    type: String,
    required: true
  },
  message: {
    type: String,
    default: () => null
  },
  messageHtml: {
    type: Boolean,
    default: () => false
  },
  caption: {
    type: String,
    default: () => ''
  },
  closeIcon: {
    type: Boolean,
    default: () => true
  },
  loading: {
    type: Boolean,
    default: () => false
  },
  closableWhileLoading: {
    type: Boolean,
    default: () => false
  },
  actions: {
    type: Array,
    default: () => []
  },
  standalone: {
    type: Boolean,
    default: () => false
  },
  status: {
    type: String,
    default: () => null
  },
  persistent: {
    type: Boolean,
    default: () => false
  },
  maximizedBreakpoint: {
    type: Number,
    default: () => 400
  }
})

const propRefs = toRefs(props)
const emit = defineEmits(['update:modelValue', 'update:dialogReference', 'update:tab', ...useDialogPluginComponent.emits])

const q = useQuasar()

const { t } = useI18n({
  'de-de': {
    actions: {
      closeDialog: 'Dialog schließen'
    }
  }
})

let dialogRef = null
let onDialogHide = () => {}
let onDialogOk = () => {}

const maximized = computed(() => q.screen.width <= propRefs.maximizedBreakpoint.value || q.screen.height <= propRefs.maximizedBreakpoint.value)

const cardSectionPadingClassTop = computed(() => {
  if (q.screen.gt.sm) {
    return 'q-pa-lg'
  } else {
    return 'q-pa-md'
  }
})

const cardSectionPadingClassContent = computed(() => {
  if (q.screen.gt.sm) {
    return 'q-px-lg q-pt-none q-pb-lg'
  } else {
    return 'q-px-md q-pt-none q-pb-md'
  }
})

if (props.standalone) {
  const { dialogRef: dr, onDialogHide: odh, onDialogOK: ok } = useDialogPluginComponent()
  dialogRef = dr
  onDialogHide = odh
  onDialogOk = ok
} else if (props.dialogReference) {
  dialogRef = props.dialogReference
} else {
  dialogRef = ref(null)
}

function updateTab (val) {
  emit('update:tab', val)
}

const isVisible = ref(false)
watch(isVisible, v => emit('update:modelValue', v))

const orderedActions = computed(() => {
  const actionsList = cloneDeep(props.actions)

  if (q.screen.lt.sm) {
    return actionsList.reverse()
  }

  return actionsList
})

watchEffect(() => {
  isVisible.value = propRefs.modelValue.value
})

watch(dialogRef, v => emit('update:dialogReference', v))

const showCloseIcon = computed(() => props.closeIcon && (!props.loading || props.closableWhileLoading))
const isPersistent = computed(() => (!props.closableWhileLoading && props.loading) || props.persistent)

const statusProps = computed(() => {
  if (props.status === 'SUCCESS') {
    return {
      role: 'alert'
    }
  }

  if (props.status === 'ERROR') {
    return {
      role: 'alert',
      ariaLive: 'assertive'
    }
  }

  return null
})

</script>
<style lang="scss">
.base-dialog {
  h2 {
    margin: 0;
  }

  .q-panel.scroll {
    overflow: unset;
  }

  .q-card__actions--horiz > .q-btn-item + .q-btn-item {
    margin-left: 1rem;
  }

  .q-card__actions--vert > .q-btn-item + .q-btn-item {
    margin-top: 1rem;
  }

  .display-note {
    margin-top: 0;
  }

  @media (min-width: 600px) {
    .display-note {
      margin-bottom: 1.5rem;
    }

    .q-dialog__inner--minimized > div {
        max-width: 800px;
    }
  }

  &.lower-width-dialog {
    @media (min-width: 600px) {
      .q-dialog__inner--minimized > div {
          max-width: 560px;
      }
    }
  }

  @media (max-width: 599px) {
    .display-note {
      margin-bottom: 1rem;
    }
  }
}
</style>
